/* eslint-disable no-case-declarations */
import { useDelayedUnmount } from '@pancakeswap/hooks'
import { useTranslation } from '@pancakeswap/localization'
import { Box, Card, Flex, Text, useMatchBreakpoints } from '@pancakeswap/uikit'
import { FarmWidget } from '@pancakeswap/widgets-internal'
import { createElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { styled } from 'styled-components'

import { V2Farm, V3Farm } from 'views/Farms/FarmsV3'
import { FarmV3ApyButton } from '../FarmCard/V3/FarmV3ApyButton'
import { ActionPanelV2, ActionPanelV3 } from './Actions/ActionPanel'
import Apr, { AprProps } from './Apr'
import { FarmCell } from './Farm'

const { FarmAuctionTag, BoostedTag, StableFarmTag, V2Tag, V3FeeTag } = FarmWidget.Tags
const { CellLayout, Details, Multiplier, Liquidity, Earned, LpAmount, StakedLiquidity } = FarmWidget.FarmTable
const { DesktopColumnSchema, MobileColumnSchema, V3DesktopColumnSchema } = FarmWidget

export type RowProps = {
  earned: FarmWidget.FarmTableEarnedProps
  initialActivity?: boolean
  multiplier: FarmWidget.FarmTableMultiplierProps
} & (V2RowProps | V3RowProps | CommunityRowProps)

export type V2RowProps = {
  type: 'v2'
  farm: FarmWidget.FarmTableFarmTokenInfoProps & { version: 2 }
  liquidity: FarmWidget.FarmTableLiquidityProps
  apr: AprProps
  details: V2Farm
}

export type CommunityRowProps = Omit<V2RowProps, 'type'> & {
  type: 'community'
}

export type V3RowProps = {
  type: 'v3'
  apr: {
    value: string
    pid: number
  }
  stakedLiquidity: FarmWidget.FarmTableLiquidityProps
  farm: FarmWidget.FarmTableFarmTokenInfoProps & { version: 3 }
  details: V3Farm
  availableLp: {
    pid: number
    amount: number
  }
  stakedLp: {
    pid: number
    amount: number
  }
}

type RowPropsWithLoading = {
  isHeader?: boolean
  userDataReady: boolean
} & RowProps

const cells = {
  apr: Apr,
  farm: FarmCell,
  earned: Earned,
  details: Details,
  multiplier: Multiplier,
  liquidity: Liquidity,
  stakedLiquidity: StakedLiquidity,
  availableLp: LpAmount,
  stakedLp: LpAmount,
}

const StyledCard = styled(Box)`
  background: ${({ theme }) => theme.colors.backgroundAlt};
  box-shadow: 0 16px 40px -16px rgba(0, 0, 0, 0.6);
  padding: 10px;
  border-radius: 24px;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  transition: all 200ms ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(139, 92, 246, 0.4);
  }
`

const CellInner = styled.div`
  // padding: 14px 0px;
  display: flex;
  width: 100%;
  align-items: center;
  // padding-right: 8px;

  ${({ theme }) => theme.mediaQueries.xl} {
    // padding-right: 32px;
  }
`

const StyledHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 200px;
  padding: 16px 48px;
`

const StyledTr = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 200px;
  cursor: pointer;
  padding: 16px 28px;
  border-radius: inherit;
  transition: background-color 200ms ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.tertiary};
  }
`

const EarnedMobileCell = styled.td`
  padding: 16px 0 24px 16px;
`

const AprMobileCell = styled.td`
  padding-top: 16px;
  padding-bottom: 24px;
`

const FarmMobileCell = styled.td`
  padding-top: 24px;
`

export const Header = () => {
  const { isDesktop, isMobile } = useMatchBreakpoints()

  const isSmallerScreen = !isDesktop
  const tableSchema = useMemo(() => {
    return isSmallerScreen ? MobileColumnSchema : DesktopColumnSchema
  }, [isSmallerScreen])
  return (
    <StyledHeader>
      {
        tableSchema.map((column) => {
          return <Text fontSize={14} color="textSubtle">{column.label}</Text>
        })
      }
    </StyledHeader>
  )
}

const Row: React.FunctionComponent<React.PropsWithChildren<RowPropsWithLoading>> = (props) => {
  const { initialActivity, userDataReady, farm, multiplier } = props
  const hasSetInitialValue = useRef(false)
  const hasStakedAmount = farm.isStaking || false
  const [actionPanelExpanded, setActionPanelExpanded] = useState(hasStakedAmount)
  const shouldRenderChild = useDelayedUnmount(actionPanelExpanded, 300)
  const { t } = useTranslation()

  const toggleActionPanel = useCallback(() => {
    setActionPanelExpanded(!actionPanelExpanded)
  }, [actionPanelExpanded])

  useEffect(() => {
    setActionPanelExpanded(hasStakedAmount)
  }, [hasStakedAmount])
  useEffect(() => {
    if (initialActivity && hasSetInitialValue.current === false) {
      setActionPanelExpanded(initialActivity)
      hasSetInitialValue.current = true
    }
  }, [initialActivity])

  const tableSchema = useMemo(() => {
    return props.type === 'v3' ? V3DesktopColumnSchema : DesktopColumnSchema
  }, [props.type])
  const columnNames = useMemo(() => tableSchema.map((column) => column.name), [tableSchema])

  return (
    <StyledCard mb={4}>
      <StyledTr onClick={toggleActionPanel}>
        {Object.keys(props).map((key) => {
          const columnIndex = columnNames.indexOf(key)
          if (columnIndex === -1) {
            return null
          }

          switch (key) {
            case 'type':
              return (
                <CellInner key={key} style={{ minWidth: '140px', gap: '4px' }}>
                  {(props[key] === 'community' || props?.farm?.isCommunity) && <FarmAuctionTag scale="sm" />}
                  {props.type === 'v2' ? (
                    props?.details?.isStable ? (
                      <StableFarmTag scale="sm" />
                    ) : (
                      <V2Tag scale="sm" />
                    )
                  ) : null}
                  {props.type === 'v3' && <V3FeeTag feeAmount={props.details.feeAmount} scale="sm" />}
                  {props?.details?.boosted && props.type === 'v3' ? <BoostedTag scale="sm" /> : null}
                </CellInner>
              )
            case 'details':
              return (
                <CellInner 
                  key={key}
                  style={{
                    justifyContent: props.type !== 'v3' ? 'flex-end' : 'center',
                  }}
                >
                  <CellLayout>
                    <Details actionPanelToggled={actionPanelExpanded} />
                  </CellLayout>
                </CellInner>
              )
            case 'apr':
              if (props.type === 'v3') {
                return (
                  <CellInner key={key}>
                    <CellLayout label={t('APR')}>
                      <FarmV3ApyButton farm={props.details} />
                    </CellLayout>
                  </CellInner>
                )
              }

              return (
                <CellInner key={key}>
                  <CellLayout label={t('APR')}>
                    <Apr
                      {...props.apr}
                      // hideButton={isSmallerScreen}
                      strikethrough={false}
                      boosted={false}
                      farmCakePerSecond={multiplier?.farmCakePerSecond}
                      totalMultipliers={multiplier?.totalMultipliers}
                    />
                    {/* {props?.details?.boosted && userDataReady ? (
                      <BoostedApr
                        lpRewardsApr={props?.apr?.lpRewardsApr}
                        apr={props?.apr?.originalValue}
                        pid={props.farm?.pid}
                        lpTotalSupply={props.details?.lpTotalSupply}
                        userBalanceInFarm={
                          stakedBalance.plus(tokenBalance).gt(0)
                            ? stakedBalance.plus(tokenBalance)
                            : proxy.stakedBalance.plus(proxy.tokenBalance)
                        }
                      />
                    ) : null} */}
                  </CellLayout>
                </CellInner>
              )
            default:
              if (cells[key]) {
                return (
                  <CellInner key={key}>
                    <CellLayout label={t(tableSchema[columnIndex].label)}>
                      {createElement(cells[key], { ...props[key], userDataReady })}
                    </CellLayout>
                  </CellInner>
                )
              }
              return null
          }
        })}
        {/* {shouldRenderChild && (
          {props.type === 'v3' ? (
            <ActionPanelV3 {...props} expanded={actionPanelExpanded} alignLinksToRight={isMobile} />
          ) : (
            <ActionPanelV2 {...props} expanded={actionPanelExpanded} alignLinksToRight={isMobile} />
          )}
        )} */}
      </StyledTr>
      {shouldRenderChild && (
        props.type === 'v3' ? (
          <ActionPanelV3 {...props} expanded={actionPanelExpanded} />
        ) : (
          <ActionPanelV2 {...props} expanded={actionPanelExpanded} />
        )
      )}
    </StyledCard>
  )
}

export default Row
